import { z } from 'zod';

/**
 * The request/response envelopes the edge (`@hono/zod-validator`) and clients
 * share. Generic and minimal — every JSON API response is either a `success`
 * envelope wrapping typed data or an `error` envelope with a stable code. Build
 * concrete schemas per route by passing the payload schema to these factories.
 */

/** The error shape inside a failed response — mirrors `AppError` (code + message). */
export const ApiError = z.object({
  code: z.string().min(1),
  message: z.string(),
  /** Optional field-level detail (e.g. flattened Zod issues). */
  details: z.record(z.string(), z.unknown()).optional(),
});
export type ApiError = z.infer<typeof ApiError>;

/**
 * Wrap a payload schema into a discriminated success/error response envelope.
 * `ok: true` carries `data`; `ok: false` carries `error`.
 */
export function apiResponse<T extends z.ZodTypeAny>(data: T) {
  return z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), data }),
    z.object({ ok: z.literal(false), error: ApiError }),
  ]);
}
export type ApiResponse<T> = { ok: true; data: T } | { ok: false; error: ApiError };

/** Cursor pagination request — opaque `cursor` + bounded page size. */
export const PageQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type PageQuery = z.infer<typeof PageQuery>;

/** Wrap an item schema into a cursor-paginated list page. */
export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    /** Next-page cursor, or `null` when the list is exhausted. */
    nextCursor: z.string().nullable(),
  });
}
export type Page<T> = { items: T[]; nextCursor: string | null };
